import { Component } from '@angular/core';
import { AlertController, NavController, NavParams, ToastController } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { Globals } from '../../../../globals';
import { TakeawayServices } from '../../../takeaway/services';
import { HttpClient, HttpParams } from '@angular/common/http';

@Component({
    selector: 'page-configuration-integration-glovo',
    templateUrl: 'configuration-integration-glovo.html',
})
export class ConfigurationIntegrationGlovoPage {
    store_id = '';
    glovoUploadMenuTitleStatus: string | null = null;
    glovoUploadMenuSubtitleStatus: string | null = null;
    glovoMenuTitleStatus: string | null = null;
    glovoMenuSubtitleStatus: string | null = null;
    uploadingMenu = false;
    verifyingMenu = false;
    //
    glovoUpdateItemsTitleStatus: string | null = null;
    glovoUpdateItemsSubtitleStatus: string | null = null;
    glovoItemsTitleStatus: string | null = null;
    glovoItemsSubtitleStatus: string | null = null;
    uploadingItems = false;
    verifyingItems = false;


    private readonly FALLBACK_TITLE = 'general-menu-estado-desconocido';
    private readonly FALLBACK_SUBTITLE = 'general-menu-estado-desconocido-descripcion';

    constructor(
        public navCtrl: NavController,
        public navParams: NavParams,
        private translate: TranslateService,
        private globals: Globals,
        private toastCtrl: ToastController,
        private takeawayServices: TakeawayServices,
        private http: HttpClient,
        private alertCtrl: AlertController) {
        this.initializeStoreId();
    }

    private initializeStoreId(): void {
        const savedStoreId = this.globals.restaurant ? this.globals.restaurant.glovo_store_id : null;
        if (savedStoreId) {
            this.store_id = savedStoreId;
        }
    }

    async save(goBack = true): Promise<void> {
        const trimmedStoreId = this.store_id.trim();
        const currentStoreId = this.globals.restaurant.glovo_store_id;

        if (trimmedStoreId && trimmedStoreId !== currentStoreId) {
            try {
                await this.updateStoreId(trimmedStoreId);
                this.showSuccessToast('general-guardado-correctamente');
            } catch (error) {
                return this.showErrorToast('general-error-al-guardar');
            }
        }

        if (goBack) {
            this.navCtrl.pop();
        }
    }

    private async updateStoreId(storeId: string): Promise<void> {
        await this.takeawayServices.afs
            .collection('restaurants')
            .doc(this.globals.restaurant_uid)
            .update({ glovo_store_id: storeId });

        this.globals.restaurant.glovo_store_id = storeId;
    }

    async changeActive(active: boolean): Promise<void> {
        if (!this.store_id.trim()) {
            return this.showErrorToast('instant-completa-todos-campos');
        }

        try {
            await this.updateGlovoStatus(active);
            this.save(false);
        } catch (error) {
            this.showErrorToast('general-error-al-guardar');
        }
    }

    private async updateGlovoStatus(active: boolean): Promise<void> {
        await this.takeawayServices.afs
            .collection('restaurants')
            .doc(this.globals.restaurant_uid)
            .update({ glovo: active });

        this.globals.restaurant.glovo = active;
    }

    uploadMenuToGlovo(): void {
        if (!this.globals.restaurant.glovo_menu_uploaded) {
            let alert = this.alertCtrl.create({
                title: this.translate.instant('general-subir-menu-glovo-warning-title'),
                message: this.translate.instant('general-subir-menu-glovo-warning-description'),
                buttons: [
                    {
                        text: this.translate.instant('general-no'),
                        role: 'cancel',
                        handler: () => {
                        }
                    },
                    {
                        text: this.translate.instant('general-si'),
                        handler: () => {
                            this.uploadMenu();
                        }
                    }
                ]
            });
            alert.present();
        } else {
            this.uploadMenu();
        }
    }

    uploadMenu() {
        this.resetMenuStatus();
        this.uploadingMenu = true;

        const params = new HttpParams().set('public_key', this.globals.public_key);

        this.http.post(`${this.globals.analytics_path}glovo/menu/upload`, params)
            .subscribe({
                next: (response: any) => {
                    this.handleUploadMenuSuccess(response.transaction_id);
                },
                error: () => this.handleUploadMenuError(),
            });
    }

    private handleUploadMenuSuccess(transactionId: string): void {
        this.globals.restaurant.glovo_upload_menu_transaction_id = transactionId;
        this.uploadingMenu = false;
        this.setSuccessMenuMessages('general-carta-enviada-exito', 'general-carta-enviada-descripcion');
    }

    private handleUploadMenuError(): void {
        this.uploadingMenu = false;
        this.setErrorMenuMessages('general-carta-error-subida', 'general-carta-error-subida-descripcion');
    }

    checkGlovoMenuStatus(): void {
        this.resetMenuStatus();
        this.verifyingMenu = true;

        const params = new HttpParams()
            .set('public_key', this.globals.public_key)
            .set('transaction_id', this.hasGlovoMenuUploadTransactionId() ? this.globals.restaurant.glovo_upload_menu_transaction_id : '');

        this.http.post(`${this.globals.analytics_path}glovo/menu/upload/verify`, params)
            .subscribe({
                next: (response: any) => {
                    this.verifyingMenu = false;
                    if (response.status === "SUCCESS" && this.globals.restaurant.glovo_menu_uploaded === undefined) {
                        this.globals.restaurant.glovo_menu_uploaded = true;
                    }
                    this.getMenuStatusMessage(response.status);
                },
                error: () => this.handleVerificationError(),
            });
    }

    private handleVerificationError(): void {
        this.verifyingMenu = false;
        this.setErrorMenuMessages('general-carta-error-inesperado', 'general-carta-error-inesperado-descripcion');
    }

    uploadItemsUpdateToGlovo(): void {
        this.resetItemsStatus();
        this.uploadingItems = true;

        const params = new HttpParams().set('public_key', this.globals.public_key);

        this.http.post(`${this.globals.analytics_path}glovo/items/bulk/update`, params)
            .subscribe({
                next: (response: any) => {
                    this.handleUploadItemsSuccess(response.transaction_id);
                },
                error: () => this.handleUploadItemsError(),
            });
    }

    private handleUploadItemsSuccess(transactionId: string): void {
        this.globals.restaurant.glovo_upload_items_transaction_id = transactionId;
        this.uploadingItems = false;
        this.setSuccessItemsMessages('general-items-enviada-exito-glovo', 'general-items-enviada-glovo-descripcion');
    }

    private handleUploadItemsError(): void {
        this.uploadingItems = false;
        this.setErrorItemsMessages('general-items-error-subida-glovo', 'general-items-error-subida-glovo-descripcion');
    }

    hasGlovoMenuUploadTransactionId(): boolean {
        const id = this.globals.restaurant.glovo_upload_menu_transaction_id;
        return (!!id && id.trim() !== '');
    }

    checkGlovoItemsUploadStatus(): void {
        this.resetItemsStatus();
        this.verifyingItems = true;

        const params = new HttpParams()
            .set('public_key', this.globals.public_key)
            .set(
                'transaction_id',
                this.hasGlovoItemsUploadTransactionId()
                    ? this.globals.restaurant.glovo_upload_items_transaction_id
                    : ''
            );

        this.http.post(`${this.globals.analytics_path}glovo/items/bulk/update/verify`, params)
            .subscribe({
                next: (response: any) => {
                    this.verifyingItems = false;
                    this.getItemsStatusMessage(response.status);
                },
                error: () => this.handleItemsVerificationError(),
            });
    }

    private handleItemsVerificationError(): void {
        this.verifyingItems = false;
        this.setErrorItemsMessages('general-items-error-inesperado', 'general-items-error-inesperado-descripcion');
    }

    private getItemsStatusMessage(status: string): void {
        const statusMessages: Record<string, [string, string]> = {
            SUCCESS: ['general-items-actualizados', 'general-items-actualizados-descripcion'],
            PROCESSING: ['general-items-procesando', 'general-items-procesando-descripcion'],
            PARTIALLY_PROCESSED: ['general-items-parcial', 'general-items-parcial-descripcion'],
            NOT_PROCESSED: ['general-items-error-configuracion', 'general-items-error-configuracion-descripcion'],
            GLOVO_ERROR: ['general-items-error-glovo', 'general-items-error-glovo-descripcion'],
        };

        const [titleKey, subtitleKey] = statusMessages[status] || [this.FALLBACK_TITLE, this.FALLBACK_SUBTITLE];
        this.glovoItemsTitleStatus = this.translate.instant(titleKey);
        this.glovoItemsSubtitleStatus = this.translate.instant(subtitleKey);
    }

    private resetItemsStatus(): void {
        this.glovoUpdateItemsTitleStatus = '';
        this.glovoUpdateItemsSubtitleStatus = '';
        this.glovoItemsTitleStatus = '';
        this.glovoItemsSubtitleStatus = '';
    }

    private setSuccessItemsMessages(titleKey: string, subtitleKey: string): void {
        this.glovoUpdateItemsTitleStatus = this.translate.instant(titleKey);
        this.glovoUpdateItemsSubtitleStatus = this.translate.instant(subtitleKey);
    }

    private setErrorItemsMessages(titleKey: string, subtitleKey: string): void {
        this.glovoUpdateItemsTitleStatus = this.translate.instant(titleKey);
        this.glovoUpdateItemsSubtitleStatus = this.translate.instant(subtitleKey);
    }

    hasGlovoItemsUploadTransactionId(): boolean {
        const id = this.globals.restaurant.glovo_upload_items_transaction_id;
        return (!!id && id.trim() !== '');
    }

    getMenuStatusMessage(status: string): void {
        const statusMessages: Record<string, [string, string]> = {
            SUCCESS: ['general-menu-disponible', 'general-menu-disponible-descripcion'],
            PROCESSING: ['general-menu-procesando', 'general-menu-procesando-descripcion'],
            FETCH_MENU_INVALID_PAYLOAD: ['general-menu-error-subida', 'general-menu-error-subida-descripcion'],
            FETCH_MENU_SERVER_ERROR: ['general-menu-error-carga', 'general-menu-error-carga-descripcion'],
            FETCH_MENU_UNAUTHORIZED: ['general-menu-error-carga', 'general-menu-error-carga-descripcion'],
            NOT_PROCESSED: ['general-menu-error-generico', 'general-menu-error-generico-descripcion'],
            LIMIT_EXCEEDED: ['general-menu-limite', 'general-menu-limite-descripcion'],
            GLOVO_ERROR: ['general-menu-error-sincronizacion', 'general-menu-error-sincronizacion-descripcion'],
            SCHEDULE_CATALOG_DISABLED: ['general-menu-no-disponible', 'general-menu-no-disponible-descripcion'],
            NOT_FOUND: ['general-menu-no-disponible-resource', 'general-menu-no-disponible-resource-descripcion'],
        };

        const [titleKey, subtitleKey] = statusMessages[status] || [this.FALLBACK_TITLE, this.FALLBACK_SUBTITLE];
        this.glovoMenuTitleStatus = this.translate.instant(titleKey);
        this.glovoMenuSubtitleStatus = this.translate.instant(subtitleKey);
    }

    private resetMenuStatus(): void {
        this.glovoUploadMenuTitleStatus = '';
        this.glovoUploadMenuSubtitleStatus = '';
        this.glovoMenuTitleStatus = '';
        this.glovoMenuSubtitleStatus = '';
    }

    private async showSuccessToast(messageKey: string): Promise<void> {
        await this.showToast(this.translate.instant(messageKey));
    }

    private async showErrorToast(messageKey: string): Promise<void> {
        await this.showToast(this.translate.instant(messageKey));
    }

    private async showToast(message: string): Promise<void> {
        const toast = await this.toastCtrl.create({
            message,
            duration: 3000,
            position: 'top',
        });
        toast.present();
    }

    private setSuccessMenuMessages(titleKey: string, subtitleKey: string): void {
        this.glovoUploadMenuTitleStatus = this.translate.instant(titleKey);
        this.glovoUploadMenuSubtitleStatus = this.translate.instant(subtitleKey);
    }

    private setErrorMenuMessages(titleKey: string, subtitleKey: string): void {
        this.glovoUploadMenuTitleStatus = this.translate.instant(titleKey);
        this.glovoUploadMenuSubtitleStatus = this.translate.instant(subtitleKey);
    }
}


// WEBPACK FOOTER //
// ./src/pages/configuration/configuration-integrations/configuration-integration-glovo/configuration-integration-glovo.ts